/**
 * Visitor analytics beacon utilities.
 *
 * - sendPageViewBeacon():   awaitable, fires on every page render (anonymised IP).
 *                           Writes to ceVisitors table. Returns { visitorID, requestId }.
 * - sendConsentBeacon():    awaitable, used before form submission (full IP).
 *                           Writes to ceConsents table. Returns { visitorID, requestId }.
 *
 * Both hit the same-origin /api/analytics endpoint handled by a Lambda@Edge
 * function on the CloudFront distribution.
 *
 * Visitor ID management:
 *   - Stored in sessionStorage (tab-scoped, clears on browser close, no cookies).
 *   - If sessionStorage already contains a visitorID it is sent with the beacon;
 *     otherwise the API response's visitorID is persisted to sessionStorage.
 */

const ANALYTICS_PATH = "/api/analytics";
const VISITOR_ID_KEY = "visitorID";

const getStoredVisitorID = () => {
  try {
    return sessionStorage.getItem(VISITOR_ID_KEY);
  } catch {
    return null;
  }
};

const storeVisitorID = (id) => {
  try {
    if (id) sessionStorage.setItem(VISITOR_ID_KEY, id);
  } catch {
    // storage unavailable – silently ignore
  }
};

/**
 * Awaitable page-view beacon (legitimate interest, anonymised IP).
 * Fires on every page render. Writes to ceVisitors table.
 *
 * @param {string} assetID
 * @param {string} [phase]
 * @param {string} [pageName]  – e.g. "AssetDetails", "ShareIdeas"
 * @returns {Promise<{visitorID: string|null, requestId: string|null}>}
 */
export const sendPageViewBeacon = async (assetID, phase, pageName) => {
  try {
    const res = await fetch(ANALYTICS_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "PAGE_VIEW",
        assetID: assetID || null,
        phase: phase || null,
        pageName: pageName || window.location.pathname || "",
        referrer: document.referrer || "",
        visitorID: getStoredVisitorID(),
      }),
    });

    if (!res.ok) return { visitorID: null, requestId: null };
    const json = await res.json();
    if (json.status === "error") {
      console.warn("Page-view beacon DB write failed:", json.error);
    }
    if (json.visitorID) storeVisitorID(json.visitorID);
    return {
      visitorID: json.visitorID || null,
      requestId: json.requestId || null,
    };
  } catch (e) {
    // Analytics must never break the app.
    console.warn("Analytics beacon error", e);
    return { visitorID: null, requestId: null };
  }
};

/**
 * Awaitable consent beacon (explicit consent, full IP stored).
 * Writes to ceConsents table. Returns { visitorID, requestId }.
 *
 * @param {string} assetID
 * @param {object} [policyUrls]  – { clientTerms, clientPrivacy }
 * @param {string} [phase]
 * @param {string} [pageName]
 * @returns {Promise<{visitorID: string|null, requestId: string|null}>}
 */
export const sendConsentBeacon = async (assetID, policyUrls, phase, pageName) => {
  try {
    const res = await fetch(ANALYTICS_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "FORM_SUBMISSION",
        assetID: assetID || null,
        phase: phase || null,
        pageName: pageName || window.location.pathname || "",
        policyUrls: policyUrls || {},
        referrer: document.referrer || "",
        visitorID: getStoredVisitorID(),
      }),
    });

    if (!res.ok) return { visitorID: null, requestId: null };
    const json = await res.json();
    if (json.status === "error") {
      console.warn("Consent beacon DB write failed:", json.error);
    }
    if (json.visitorID) storeVisitorID(json.visitorID);
    return {
      visitorID: json.visitorID || null,
      requestId: json.requestId || null,
    };
  } catch (e) {
    console.warn("Consent beacon error", e);
    return { visitorID: null, requestId: null };
  }
};
